import { createStepsTokens } from '../tokens/components.js';
import { type SemanticTokens, SEMANTIC } from '../tokens/semantic.js';

/**
 * Steps Component Configuration
 *
 * - Container: white background, border-bottom divider, px-48 py-16
 * - Step circle: 32Ã—32px, fully rounded
 *   - Default: surface.raised bg (#e7e8ec), text.secondary number (#08183a)
 *   - Active:  text.tertiary bg  (#212f4e), text.onColor number  (white)
 * - Step label: Poppins Regular 14px, text.secondary, always same color
 * - Connector: border.divider horizontal line
 * - Gap between circle and label: 10px
 */
export function createSteps(s: SemanticTokens) {
  const stepsTokens = createStepsTokens(s);
  return {
  root: {
    transitionDuration: stepsTokens.base.transitionDuration,
  },
  separator: {
    background: stepsTokens.border,
  },
  itemLink: {
    borderRadius: '0',
    gap: stepsTokens.base.gap,
    focusRing: {
      width: '0',
      style: 'none',
      color: 'transparent',
      offset: '0',
      shadow: 'none',
    },
  },
  itemLabel: {
    color: stepsTokens.labelFg,
    activeColor: stepsTokens.labelFg,
    fontWeight: stepsTokens.label.fontWeight,
  },
  itemNumber: {
    size: stepsTokens.base.numberSize,
    fontSize: stepsTokens.number.fontSize,
    fontWeight: stepsTokens.number.fontWeight,
    borderRadius: stepsTokens.base.numberRadius,
    shadow: 'none',
    // Default
    background: stepsTokens.numberBg,
    borderColor: 'transparent',
    color: stepsTokens.numberFg,
    // Active
    activeBackground: stepsTokens.numberBgActive,
    activeBorderColor: 'transparent',
    activeColor: stepsTokens.numberFgActive,
  },
  css: () => `
    p-steps {
      display: block;
      width: 100%;
      background: ${stepsTokens.bg};
      border-bottom: ${stepsTokens.base.borderWidth} solid ${stepsTokens.border};
    }

    .p-steps {
      width: 100%;
      padding: ${stepsTokens.base.paddingY} ${stepsTokens.base.paddingX};
      overflow: hidden;
    }

    .p-steps-list {
      width: 100%;
      align-items: flex-start;
    }

    .p-steps-item {
      flex: 1;
      min-width: 0;
    }

    /* Separator is a ::before pseudo-element positioned absolutely.
       Pin it to the vertical center of the number circle so it never
       shifts when labels wrap to multiple lines. */
    .p-steps-item::before {
      top: calc(${stepsTokens.base.numberSize} + 1px);
      height: ${stepsTokens.base.borderWidth};
      background: ${stepsTokens.border};
    }

    .p-steps-item-link {
      gap: ${stepsTokens.base.gap};
    }

    .p-steps-item-number {
      font-family: ${stepsTokens.number.fontFamily};
      font-size: ${stepsTokens.number.fontSize};
      font-weight: ${stepsTokens.number.fontWeight};
      line-height: ${stepsTokens.number.lineHeight};
      min-width: ${stepsTokens.base.numberSize};
      min-height: ${stepsTokens.base.numberSize};
      width: ${stepsTokens.base.numberSize};
      height: ${stepsTokens.base.numberSize};
      border: none;
      flex-shrink: 0;
    }

    .p-steps-item-label {
      font-family: ${stepsTokens.label.fontFamily};
      font-size: ${stepsTokens.label.fontSize};
      font-weight: ${stepsTokens.label.fontWeight};
      line-height: ${stepsTokens.label.lineHeight};
      color: ${stepsTokens.labelFg};
      margin-top: 0;
      white-space: normal;
      text-align: center;
      word-break: break-word;
    }

    .p-steps-item.p-steps-item-active .p-steps-item-label {
      color: ${stepsTokens.labelFg};
      font-weight: ${stepsTokens.label.fontWeight};
    }

    @media (max-width: 1279px) {
      .p-steps-item-label {
        display: none;
      }
    }
  `,
  };
}
export const steps = createSteps(SEMANTIC);